import { useEffect, useState, useCallback } from 'react';
import { FiPlus, FiUsers } from 'react-icons/fi';

import PageHeader from '../Components/PageHeader/PageHeader';
import Button from '../Components/Button/Button';
import EmptyState from '../Components/EmptyState/EmptyState';
import AccountGroupCard from '../Components/AccountGroupCard/AccountGroupCard';
import PromptModal from '../Components/PromptModal/PromptModal';
import ConfirmDialog from '../Components/ConfirmDialog/ConfirmDialog';

import {
  listAccountGroups,
  createAccountGroup,
  deleteAccountGroup,
} from '../Utilities/api';
import { useAuth } from '../Utilities/authContext';

import './dashboard.css';

function Dashboard() {
  const { user } = useAuth();
  const [groups, setGroups] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null);

  const load = useCallback(async () => {
    const gs = await listAccountGroups();
    setGroups(gs);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleCreate = async ({ name, description }) => {
    await createAccountGroup({ name, description });
    await load();
  };

  const handleConfirmDelete = async () => {
    if (!pendingDelete) return;
    await deleteAccountGroup(pendingDelete.id);
    setPendingDelete(null);
    await load();
  };

  const firstName = user?.name?.split(' ')[0] || '';

  return (
    <>
      <PageHeader
        title={firstName ? `Welcome back, ${firstName}` : 'Your account groups'}
        subtitle="Organize your creative identities and the platforms they live on."
        actions={
          <Button icon={<FiPlus />} onClick={() => setShowCreate(true)}>
            New group
          </Button>
        }
      />

      {groups.length === 0 ? (
        <EmptyState
          icon={<FiUsers />}
          title="No account groups yet"
          description="Create an account group to start organizing your brands, clients, or projects."
          action={
            <Button icon={<FiPlus />} onClick={() => setShowCreate(true)}>
              Create your first group
            </Button>
          }
        />
      ) : (
        <div className="dashboard-list">
          {groups.map((g) => (
            <AccountGroupCard
              key={g.id}
              group={g}
              accountCount={g.accountCount ?? 0}
              onDelete={(gr) => setPendingDelete(gr)}
              onRefreshed={load}
            />
          ))}
        </div>
      )}

      <PromptModal
        open={showCreate}
        title="Create account group"
        submitLabel="Create group"
        nameLabel="Group name"
        namePlaceholder="e.g. Personal Brands"
        descriptionLabel="Description (optional)"
        descriptionPlaceholder="What's in this group?"
        onClose={() => setShowCreate(false)}
        onSubmit={handleCreate}
      />

      <ConfirmDialog
        open={!!pendingDelete}
        title="Delete account group?"
        message={`This will permanently delete "${pendingDelete?.name}" and every account, login, and upload inside it. This cannot be undone.`}
        confirmLabel="Delete group"
        danger
        onClose={() => setPendingDelete(null)}
        onConfirm={handleConfirmDelete}
      />
    </>
  );
}

export default Dashboard;
